// The registry routes the offline transport answers (BL-2343, ADR 066 — Offline Mode § 3), each
// typed against the route's own response DTO. Adding a key to OFFLINE_ROUTES without a handler,
// or a handler returning another shape, fails `tsc`. BL-2430 adds the screens' reads, BL-2437 the
// lot form's, BL-2431 the lot writes, BL-2447 the lot's note writes, BL-2448 the status change, Move
// and Relist.
//
// `GET /api/time` is deliberately absent: the server clock syncs once and keeps its first answer,
// so a device-clock answer offline would pin the clock to the device after Go online (BL-596).
// Unanswered, it fails as a dropped connection and the clock retries once online.
import type { adminRegistry } from '@auctogon/core-admin/api-registry';
import type { AuthMeResponseBody } from '@auctogon/core-admin/schemas/responses/session';

import { OFFLINE_SESSION_RECORD_KEY, type OfflineStore } from '../../gateways/native-device';
import type { OfflineHandler, OfflineResponseFor } from './create-offline-transport';
import { SIGNED_OUT } from './offline-answers';
import { lotFormReadHandlers } from './offline-lot-form-handlers';
import { lotPlacementHandlers } from './offline-placement-handlers';
import { screenReadHandlers } from './offline-screen-handlers';
import { noteWriteHandlers } from './offline-note-handlers';
import { lotWriteHandlers } from './offline-write-handlers';

/** Every route the offline transport answers, as `"<dotted.key>:<VERB>"`. */
export const OFFLINE_ROUTES = [
  'auth.me:GET',
  'tenants.user:GET',
  // BL-2430: the Auctions list, the auction header and Lots tab, and Inventory.
  'auctions.list:GET',
  'auctions.counts:GET',
  'auctions.filter_options:GET',
  'auctions.id.chrome:GET',
  'auctions.id.detail:GET',
  'auctions.id.bid_increments:GET',
  'tenants.id.bidder_origin:GET',
  'inventory.index:GET',
  'inventory.counts:GET',
  'inventory.filter_options:GET',
  'tenants.id.lot_custom_fields:GET',
  // BL-2437: the lot form, its pickers and the status menu's check.
  'lots.id.index:GET',
  'internal_notes.index:GET',
  'customers.search:GET',
  'locations.index:GET',
  'tenants.id.tax_rates.index:GET',
  'sales_reps.index:GET',
  'lots.batch_check:POST',
  // BL-2431: the writes, queued for the replay (BL-2345).
  'lots.id.index:PATCH',
  'lots.placeInAuction:POST',
  'inventory.index:POST',
  // BL-2447: a lot's notes, queued the same way.
  'internal_notes.index:POST',
  'internal_notes.detail:PATCH',
  'internal_notes.detail:DELETE',
  // BL-2448: the Draft ↔ Upcoming status change (Move and Relist ride the PATCH and placement above).
  'lots.batch:POST',
] as const;

/** A supported route key. */
export type SupportedOfflineRoute = (typeof OFFLINE_ROUTES)[number];

/** The raw (non-envelope) body a route answers with, for the routes the client reads via `.raw()`. */
interface OfflineRawBodies {
  'auth.me:GET': AuthMeResponseBody;
}

type RawFor<K> = K extends keyof OfflineRawBodies ? OfflineRawBodies[K] : never;

/** One handler per supported route, typed by the registry. */
export type OfflineHandlers = {
  [K in SupportedOfflineRoute]: OfflineHandler<OfflineResponseFor<typeof adminRegistry, K>, RawFor<K>>;
};

async function readSession(openStore: () => Promise<OfflineStore | null>) {
  const store = await openStore();
  if (!store) return null;
  return store.records.get({ collection: 'session', key: OFFLINE_SESSION_RECORD_KEY });
}

/**
 * The handlers, reading the stored session (ADR 066 — Offline Mode § 2). With no active scope, or
 * no stored session, the session routes answer as for a signed-out caller.
 */
export const offlineHandlers: OfflineHandlers = {
  'auth.me:GET': async ({ openStore }) => {
    const session = await readSession(openStore);
    if (!session) return { kind: 'raw', status: 401, body: { success: false, user: null } };
    return { kind: 'raw', status: 200, body: { success: true, user: session.user } };
  },
  'tenants.user:GET': async ({ openStore }) => {
    const session = await readSession(openStore);
    if (!session) return SIGNED_OUT;
    return { kind: 'ok', data: session.tenants };
  },
  ...screenReadHandlers,
  ...lotFormReadHandlers,
  ...lotWriteHandlers,
  ...noteWriteHandlers,
  // After the writes: a Move and a Relist are routed here before a Save or a create.
  ...lotPlacementHandlers,
};
